import { Injectable, inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { SKIP_THROTTLE_RETRY } from '../http/throttle-retry.context';

/**
 * ThrottleInterceptor — maneja respuestas 429 Too Many Requests.
 *
 * Estrategia (doc FGA-7 §A):
 *   1. En el primer 429, lee Retry-After y reintenta UNA vez tras el delay.
 *   2. Si el retry sigue fallando, propaga el error al ErrorInterceptor para toast.
 *   3. No acumula retries (header X-Retry-Attempt previene loops).
 *   4. FGA-61: una petición marcada con `skipThrottleRetry()` queda fuera de todo
 *      esto — ni reintento ni toast. El caso que lo motivó: un rate-limit que cuenta
 *      por device y por ventana, donde reenviar solo consume cupo que el operador no
 *      pidió gastar, y donde la pantalla ya pinta el 429 con su propia cuenta
 *      regresiva. Ver `http/throttle-retry.context.ts`.
 *
 * Debe registrarse DESPUÉS de ErrorInterceptor en el array de providers
 * para procesar respuestas ANTES que el interceptor de errores.
 */
@Injectable({ providedIn: 'root' })
export class ThrottleInterceptor implements HttpInterceptor {
  private readonly toast = inject(MessageService);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((err: HttpErrorResponse) => {
        if (
          err.status === 429 &&
          !req.headers.has('X-Retry-Attempt') &&
          !req.context.get(SKIP_THROTTLE_RETRY)
        ) {
          const retryAfterSec = Number(err.headers.get('Retry-After') ?? '5');

          this.toast.add({
            severity: 'info',
            summary: 'Demasiadas solicitudes',
            detail: `Reintentando en ${retryAfterSec}s…`,
            life: (retryAfterSec + 1) * 1000,
          });

          return timer(retryAfterSec * 1000).pipe(
            switchMap(() => next.handle(req.clone({ setHeaders: { 'X-Retry-Attempt': '1' } }))),
          );
        }
        return throwError(() => err);
      }),
    );
  }
}
